import React from 'react';
import {
  Edit,
  TextField,
  DateField,
  ReferenceField,
  usePermissions,
  SelectField,
  ChipField,
  ReferenceArrayField,
  SingleFieldList,
  TabbedShowLayout,
  Tab,
  useRecordContext,
} from 'react-admin';

import {
  Box,
  Card,
  Grid,
  Typography,
  CardHeader,
  CardContent,
  Divider,
} from '@mui/material';

import UpdateRecommendationField from '../../customFields/UpdateRecommendationField';
import StatusField from '../../customFields/StatusField';
import ReprocessRequest from '../../customFields/ReprocessRequest';

import { requestEditMap } from '../../constants/requestFeatures';
import requestTypes from '../../constants/requestTypes';

import hasAccess from '../../utilities/hasAccess';

import RequestBreadcrumb from './RequestBreadcrumb';
import actions, { Actions } from '../../constants/actions';
import CommentList from '../comment/CommentList';
import { IRequest } from './types';
import useValidateId from '../../hooks/useValidateId';
import resourceTypes from '../../constants/resourceTypes';
import PermissionMissing from '../utils/PermissionMissing';
import EditToolbar from '../layout/EditToolbar';
import AuditResourceList from '../audit/AuditResourceList';

const RequestExtraInformation = () => {
  const record = useRecordContext<IRequest>();
  if (!record) return null;
  const RequestEditExtraInformationComponent = requestEditMap[record.requestType];
  if (!RequestEditExtraInformationComponent) return null;
  return <RequestEditExtraInformationComponent />;
};

const RequestEdit = () => {
  const { permissions } = usePermissions<Actions[]>();
  const validatedId = useValidateId();
  if (!validatedId.isValid) return null;

  return (
    <Edit
      mutationMode="pessimistic"
      redirect={false}
      actions={(
        <EditToolbar>
          {hasAccess(permissions, actions.REQUEST_UPDATE_RECOMMENDATION) && (
            <UpdateRecommendationField />
          )}
          {hasAccess(permissions, actions.REQUEST_REPROCESS) && <ReprocessRequest />}
        </EditToolbar>
      )}
    >
      <RequestBreadcrumb />
      <TabbedShowLayout>
        <TabbedShowLayout.Tab label="Information">
          <Grid container spacing={6}>
            <Grid
              size={{
                xs: 12,
                lg: 6,
              }}
            >
              <Card variant="outlined">
                <CardHeader
                  title="Request details"
                />
                <Divider />
                <CardContent>
                  <Grid container spacing={6}>
                    <Grid size={6}>
                      <Box display="flex" flexDirection="column">
                        <Typography variant="smallTitle">Id</Typography>
                        <TextField source="id" label="ID" />
                      </Box>
                    </Grid>
                    <Grid size={6}>
                      <Box display="flex" flexDirection="column">
                        <Typography variant="smallTitle">Custom ID</Typography>
                        <TextField source="customId" emptyText="-" />
                      </Box>
                    </Grid>
                    <Grid size={6}>
                      <Box display="flex" flexDirection="column">
                        <Typography variant="smallTitle">Recommendation</Typography>
                        <StatusField source="recommendation" />
                      </Box>
                    </Grid>
                    <Grid size={6}>
                      <Box display="flex" flexDirection="column">
                        <Typography variant="smallTitle">Request type</Typography>
                        <SelectField source="requestType" choices={Object.values(requestTypes)} />
                      </Box>
                    </Grid>
                    <Grid size={6}>
                      <Box display="flex" flexDirection="column">
                        <Typography variant="smallTitle">Created at</Typography>
                        <DateField source="createdAt" showTime />
                      </Box>
                    </Grid>
                    <Grid size={6}>
                      <Box display="flex" flexDirection="column">
                        <Typography variant="smallTitle">Process</Typography>
                        {hasAccess(permissions, actions.PROCESS_GET) ? (
                          <ReferenceField reference="processes" source="process">
                            <TextField source="name" />
                          </ReferenceField>
                        ) : (<PermissionMissing />)}
                      </Box>
                    </Grid>
                    <Grid size={12}>
                      <Box display="flex" flexDirection="column">
                        <Typography variant="smallTitle">Signals</Typography>
                        <ReferenceArrayField source="signals" reference="signals" empty="-">
                          <SingleFieldList>
                            <ChipField size="small" source="name" />
                          </SingleFieldList>
                        </ReferenceArrayField>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid
              size={{
                xs: 12,
                lg: 6,
              }}
            >
              <CommentList source="comments" action={actions.REQUEST_ADD_COMMENT} deleteAction={actions.REQUEST_DELETE_COMMENT} />
            </Grid>
            <RequestExtraInformation />
          </Grid>
        </TabbedShowLayout.Tab>
        {hasAccess(permissions, actions.AUDIT_TRACK_LIST) && (
          <Tab label="Audit trail" path="audit-tracks">
            <AuditResourceList resourceType={resourceTypes.REQUEST} />
          </Tab>
        )}
      </TabbedShowLayout>
    </Edit>
  );
};

export default RequestEdit;
